import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ERROR_DISPLAY_DURATION = 10000;
const SUCCESS_LIVE_REGION_DURATION = 5000;

/**
 * Custom element for bundle products.
 *
 * Reads child variant IDs from an embedded JSON script tag, intercepts the
 * Add to Cart click, and POSTs all available variants in one request via
 * /cart/add.js. Fires CartAddEvent on success so the cart drawer and icon
 * update automatically through the existing theme infrastructure.
 *
 * @typedef {Object} BundleAtcRefs
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} errorText
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement} quantitySelector
 *
 * @extends {Component<BundleAtcRefs>}
 */
class BundleAtcComponent extends Component {
  requiredRefs = ['addToCartButton', 'errorMessage', 'errorText', 'liveRegion'];

  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onPlanChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onPlanChange);
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  /**
   * Toggles the selected class on plan cards when a radio changes.
   * @param {Event} event
   */
  #onPlanChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    this.querySelectorAll('[data-plan-card]').forEach((card) => {
      card.classList.remove('bundle-atc__plan-card--selected');
    });

    event.target.closest('[data-plan-card]')?.classList.add('bundle-atc__plan-card--selected');
  };

  /**
   * Returns parsed bundle items from the embedded JSON script tag.
   * @returns {{ variant_id: number, title: string, available: boolean, selling_plan_id: number | null }[]}
   */
  #getBundleItems() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      console.error('[BundleATC] Could not parse bundle items JSON.');
      return [];
    }
  }

  /**
   * Whether the customer opted into a subscription plan card (vs one-time).
   * The checked radio's value is NOT a valid selling plan ID for every child
   * product — each item carries its own selling_plan_id instead.
   * @returns {boolean}
   */
  #wantsSubscription() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked')
    );
    return !!checked?.value;
  }

  /**
   * Returns current quantity from the stepper input.
   * @returns {number}
   */
  #getQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="quantity"]')
    );
    const min = Number(input?.min) || 1;
    return Math.max(min, Number(input?.value) || min);
  }

  /**
   * Collects section IDs from cart-items-component elements so the cart
   * drawer receives updated HTML after the add.
   * @returns {string}
   */
  #getCartSections() {
    const ids = /** @type {string[]} */ ([]);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids.join(',');
  }

  /** @param {string} text */
  #showError(text) {
    const { errorMessage, errorText, liveRegion } = this.refs;

    errorText.textContent = text;
    errorMessage.classList.remove('hidden');
    liveRegion.textContent = text;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      errorMessage.classList.add('hidden');
      liveRegion.textContent = '';
    }, ERROR_DISPLAY_DURATION);
  }

  #clearError() {
    this.refs.errorMessage.classList.add('hidden');
  }

  /**
   * Main handler called when the Add to Cart button is clicked.
   * Bound via on:click="/handleAddToCart" in the Liquid template.
   */
  async handleAddToCart() {
    const allItems = this.#getBundleItems();
    const availableItems = allItems.filter((item) => item.available);

    if (availableItems.length === 0) return;

    const { addToCartButton, liveRegion } = this.refs;
    const wantsSubscription = this.#wantsSubscription();
    const quantity = this.#getQuantity();
    const bundleTitle = this.dataset.productTitle ?? '';
    const sections = this.#getCartSections();

    addToCartButton.disabled = true;
    this.#clearError();

    const payload = {
      items: availableItems.map((item) => ({
        id: item.variant_id,
        quantity,
        ...(wantsSubscription && item.selling_plan_id ? { selling_plan: item.selling_plan_id } : {}),
        properties: { Bundle: bundleTitle },
      })),
      ...(sections ? { sections } : {}),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        // Shopify returned an error (e.g. item unavailable, qty exceeded)
        this.#showError(data.message ?? 'Could not add items to cart.');

        this.dispatchEvent(
          new CartErrorEvent(this.id, data.message, data.description, data.errors)
        );
      } else {
        // Success — fire CartAddEvent so the cart drawer + icon update
        liveRegion.textContent = Theme.translations.added ?? 'Added to cart';
        setTimeout(() => {
          liveRegion.textContent = '';
        }, SUCCESS_LIVE_REGION_DURATION);

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'bundle-atc-component',
            itemCount: quantity * availableItems.length,
            productId: this.dataset.productId,
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      console.error('[BundleATC] Fetch error:', error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      addToCartButton.disabled = false;
    }
  }
}

if (!customElements.get('bundle-atc-component')) {
  customElements.define('bundle-atc-component', BundleAtcComponent);
}
